import { useId } from "react";
import { Button } from "../../components/Button";
import "./services.css";

export interface EnvOverrideRow {
  /** Stable per-row key for React reconciliation only — independent of the
   * (editable) env var name in `key`, so retyping a duplicate name doesn't
   * scramble which row the operator is editing. */
  id: string;
  key: string;
  value: string;
}

let rowSeq = 0;
export function newEnvOverrideRow(key = "", value = ""): EnvOverrideRow {
  rowSeq += 1;
  return { id: `row-${rowSeq}`, key, value };
}

/** Converts the editor's rows to the wire shape, dropping rows with a blank
 * key (an operator who added a row and abandoned it shouldn't send `"": ""`)
 * and keeping the last value if a key is repeated (the same "last write
 * wins" an object literal would give). */
export function rowsToRecord(rows: EnvOverrideRow[]): Record<string, string> {
  const record: Record<string, string> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) continue;
    record[key] = row.value;
  }
  return record;
}

export function recordToRows(record: Record<string, string>): EnvOverrideRow[] {
  return Object.entries(record).map(([key, value]) => newEnvOverrideRow(key, value));
}

interface EnvOverridesEditorProps {
  rows: EnvOverrideRow[];
  onChange: (rows: EnvOverrideRow[]) => void;
  disabled?: boolean;
}

/**
 * Key/value editor for a placement's `envOverrides` — a per-node layer on
 * top of the service's own `env` (ADR-0025 §2). Deliberately not a secret
 * editor: overrides are plain values, never secret refs (secrets stay
 * write-only through the existing secrets store — S-13).
 */
export function EnvOverridesEditor({ rows, onChange, disabled }: EnvOverridesEditorProps) {
  const legendId = useId();

  function updateRow(id: string, patch: Partial<Pick<EnvOverrideRow, "key" | "value">>) {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function removeRow(id: string) {
    onChange(rows.filter((row) => row.id !== id));
  }

  function addRow() {
    onChange([...rows, newEnvOverrideRow()]);
  }

  return (
    <div className="env-overrides-editor">
      <span id={legendId} className="form-field__label">
        Environment overrides
      </span>
      {rows.length === 0 && (
        <p className="form-field__hint">No overrides — this placement uses the service's own environment as-is.</p>
      )}
      {rows.map((row) => (
        <div className="env-overrides-editor__row" key={row.id}>
          <label className="visually-hidden" htmlFor={`${row.id}-key`}>
            Override name
          </label>
          <input
            id={`${row.id}-key`}
            type="text"
            placeholder="KEY"
            autoComplete="off"
            spellCheck={false}
            value={row.key}
            onChange={(e) => updateRow(row.id, { key: e.target.value })}
            disabled={disabled}
          />
          <label className="visually-hidden" htmlFor={`${row.id}-value`}>
            Override value
          </label>
          <input
            id={`${row.id}-value`}
            type="text"
            placeholder="value"
            autoComplete="off"
            spellCheck={false}
            value={row.value}
            onChange={(e) => updateRow(row.id, { value: e.target.value })}
            disabled={disabled}
          />
          <Button
            type="button"
            variant="ghost"
            onClick={() => removeRow(row.id)}
            disabled={disabled}
            aria-label={`Remove override${row.key ? ` "${row.key}"` : ""}`}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button type="button" variant="secondary" onClick={addRow} disabled={disabled}>
        Add override
      </Button>
    </div>
  );
}
